import React, { useEffect, useState } from 'react';
import { Container, Typography, Button, Box, Divider, Fab } from '@mui/material';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { motion } from 'framer-motion';
import ContentCreators from './ContentCreators';
import ParticleBackground from '../components/common/ParticleBackground';
import SponsorCarousel from '../components/SponsorCarousel';

const getGradientByTime = () => {
  const hour = new Date().getHours();
  if (hour >= 7 && hour <= 18) {
    return 'linear-gradient(45deg, #ff6200, #000)';
  } else {
    return 'linear-gradient(45deg, #000, #ff6200)';
  }
};

const Section = ({ children, id }) => (
  <Box
    id={id}
    sx={{
      position: 'relative',
      overflowX: 'hidden',
      background: 'transparent',
      backgroundAttachment: 'fixed',
      py: 8,
      zIndex: 1
    }}
    component={motion.section}
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.6 }}
  >
    <Container maxWidth="lg">
      {children}
    </Container>
  </Box>
);

export default function HomePage() {
  const [showScroll, setShowScroll] = useState(false);

const handleScroll = () => {
  setShowScroll(window.scrollY > 200);
};

useEffect(() => {
  window.addEventListener('scroll', handleScroll);
  return () => window.removeEventListener('scroll', handleScroll);
}, []);

  const [bgGradient, setBgGradient] = useState(getGradientByTime());

useEffect(() => {
  const interval = setInterval(() => {
    setBgGradient(getGradientByTime());
  }, 60000);

  return () => clearInterval(interval);
}, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const DecorativeDivider = () => (
    <Divider
      sx={{
        height: '2px',
        my: 4,
        background: 'linear-gradient(to right, transparent, #ff6a00, transparent)',
        borderRadius: '1px',
        filter: 'drop-shadow(0 0 4px #ff6a00)',
        border: 'none'
      }}
    />
  );

  return (
    <Box
      sx={{
        position: 'relative',
        overflowX: 'hidden',
        background: bgGradient,
        backgroundAttachment: 'fixed',
        animation: 'bg-animation 10s infinite alternate',
        '@keyframes bg-animation': {
          '0%': { background: 'linear-gradient(45deg, #ff6200, #000)' },
          '100%': { background: 'linear-gradient(45deg, #000, #ff6200)' }
        }
      }}
    >
      {/* Overlay para mejorar contraste */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background: 'rgba(0,0,0,0.5)',
          zIndex: 0
        }}
      />

      <ParticleBackground />

      {/* Hero Section */}
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          textAlign: 'center',
          background: 'transparent',
          zIndex: 1,
          position: 'relative'
        }}
        component={motion.div}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      >
        <Typography
          variant="h2"
          component={motion.h1}
          initial={{ scale: 0.95 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.3, duration: 0.6 }}
          sx={{fontSize: '5.25rem', fontWeight: 700, color: '#fff', mb: 2 }}
        >
          ¡Bienvenid@ a Rebel Fox Gaming!
        </Typography>
        <Typography variant="h5" sx={{fontSize: '2.5rem', mb: 4, color: '#e0e0e0' }}>
          Donde el juego y la pasión se cruzan.
        </Typography>
        <motion.div whileHover={{ scale: 1.1 }}>
          <Button
            variant="contained"
            color="warning"
            href="https://www.instagram.com/p/DW7JmTEllZQ/?hl=es-la&img_index=1"
            sx={{ fontWeight: 'bold', fontSize: '1.2rem', boxShadow: '0 0 10px #ff6a00' }}
          >
            Inscribite a la última actividad
          </Button>
        </motion.div>
      </Box>

      {/* Sección: Quienes Somos */}
      <Section id="quienes-somos">
        <Typography
          variant="h2"
          align='center'
          component={motion.h1}
          initial={{ scale: 0.95 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.3, duration: 0.6 }}
          sx={{ fontWeight: 900, color: '#fff', mb: 2 }}
        >
          #SOMOS-REBEL
        </Typography>

        <Box
          sx={{
            background: 'rgba(0,0,0,0.4)',
            backdropFilter: 'blur(6px)',
            borderRadius: '16px',
            p: 3,
            maxWidth: '2000px',
            margin: '0 auto'
          }}
        >
          <Typography sx={{ textAlign: 'center', fontSize: '2.25rem', mb: 2, color: '#e0e0e0', lineHeight: 1.8, letterSpacing: '0.3px', textShadow: '0 0 8px rgba(0,0,0,0.6)' }}>
            Club de E-Sports multidisciplinario que compite en diferentes juegos, formando equipos y jugadores hacia lo profesional.
          </Typography>

          <Typography sx={{ textAlign: 'center', fontSize: '2.25rem', mb: 2, color: '#e0e0e0', lineHeight: 1.8, letterSpacing: '0.3px', textShadow: '0 0 8px rgba(0,0,0,0.6)' }}>
            También contamos con creadores de contenido e influencers que potencian nuestra comunidad.
          </Typography>

          <Typography sx={{ textAlign: 'center', fontSize: '2.25rem', color: '#e0e0e0', lineHeight: 1.8, letterSpacing: '0.3px', textShadow: '0 0 8px rgba(0,0,0,0.6)' }}>
            Ofrecemos múltiples servicios relacionados con gaming, marketing, eventos online y presenciales, soluciones y asesorías.
          </Typography>
        </Box>

          
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
        <Button 
            variant="outlined" 
            color="warning" 
            href="/about" 
            sx={{ fontSize: '1.25rem' }}
          >
        Ver más
        </Button>
        </Box>

      </Section>

      <DecorativeDivider />

      {/* Sección: Creadores */}
      <Section id="creadores">
        <Typography variant="h4" gutterBottom sx={{ fontSize: '3.25rem', fontWeight: 900, textAlign:'center', color: '#fff', mb: 2 }} className="font-graffiti">
          #CREADORES-REBEL
        </Typography>

        <Box sx={{
          background: 'rgba(0,0,0,0.4)',
          backdropFilter: 'blur(6px)',
          borderRadius: '16px',
          p: 3,
          maxWidth: '2000px'
        }}>
          <Typography sx={{fontSize: '2.25rem', fontWeight: 400, align: 'center', color: '#e0e0e0', lineHeight: 1.8, letterSpacing: '0.3px', textShadow: '0 0 8px rgba(0,0,0,0.6)' }}>
            Descubrí el contenido increíble que crean nuestros talentos.
          </Typography>
        </Box>

        <ContentCreators />

        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
        <Button 
        href="/creators"
        variant="outlined" 
        color="warning" 
        sx={{ fontSize: '1.25rem' }}
        >
        Ver creadores
        </Button>
        </Box>

      </Section>

      <DecorativeDivider />

      {/* Sección: Noticias */}
      <Section id="noticias">
        <Typography variant="h4" gutterBottom sx={{fontSize: '4.25rem', fontWeight: 900, textAlign:'center', color: '#fff', mb: 2 }} className="font-graffiti">
          #NOTICIAS
        </Typography>

        <Box sx={{
          background: 'rgba(0,0,0,0.4)',
          backdropFilter: 'blur(6px)',
          borderRadius: '16px',
          p: 3,
          maxWidth: '2000px',
          margin: '0 auto',
          textAlign: 'center'
        }}>
          <Typography sx={{ fontSize: '2.25rem', fontWeight: 400, color: '#e0e0e0', lineHeight: 1.8, letterSpacing: '0.3px', textShadow: '0 0 8px rgba(0,0,0,0.6)' }}>
            Enterate de los últimos torneos, logros y noticias de la comunidad.
          </Typography>

          <Typography sx={{ fontSize: '2.25rem', fontWeight: 400, color: '#e0e0e0', lineHeight: 1.8, letterSpacing: '0.3px', textShadow: '0 0 8px rgba(0,0,0,0.6)' }}>
            Subscribite a nuestro newsletter y recibí las novedades al instante.
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Button 
          variant="outlined" 
          color="warning" 
          href="/news" 
          sx={{ fontSize: '1.25rem' }}
          >
          Ir a Noticias
        </Button>
      </Box>

      </Section>

      <DecorativeDivider />

      {/* Sección: Sponsors */}
      <Section id="sponsors">
        <Typography
          variant="h2"
          component={motion.h1}
          align='center'
          initial={{ scale: 0.95 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.3, duration: 0.6 }}
          sx={{ fontWeight: 700, color: '#fff', mb: 2 }}
        >
          #SPONSORS
        </Typography>
        <SponsorCarousel />
      </Section>

      <DecorativeDivider />

      {/* Sección: Tienda */}
      <Section id="tienda">
        <Typography variant="h4" gutterBottom sx={{fontSize: '3.25rem', fontWeight: 900, textAlign:'center', color: '#fff', mb: 2 }} className="font-graffiti">
          #TIENDA-REBEL
        </Typography>

        <Box sx={{
          background: 'rgba(0,0,0,0.4)',
          backdropFilter: 'blur(6px)',
          borderRadius: '16px',
          p: 3,
          maxWidth: '2000px',
          margin: '0 auto',
          textAlign: 'center'
        }}>
          <Typography sx={{fontSize: '2.25rem', fontWeight: 400, textAlign: 'center', color: '#e0e0e0', lineHeight: 1.8, letterSpacing: '0.3px', textShadow: '0 0 8px rgba(0,0,0,0.6)' }}>
            Conocé la indumentaria oficial de Rebel Fox Gaming.
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
        <Button 
              variant="outlined" 
              color="warning" 
              href="/shop" 
              sx={{ fontSize: '1.25rem' }}
        >
        Ir a la Tienda
      </Button>
      </Box>
      </Section>

      {showScroll && (
        <Fab
          color="warning"
          size="medium"
          onClick={scrollToTop}
          sx={{ position: 'fixed', bottom: 32, right: 32, boxShadow: '0 0 20px #ff6a00' }}
          component={motion.div}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          whileHover={{ scale: 1.2 }}
        >
          <KeyboardArrowUpIcon />
        </Fab>
      )}

      <Box sx={{ backgroundColor: '#000', py: 4, textAlign: 'center' }}>
        <Typography variant="body2" sx={{ color: '#777' }}>
          © 2025 Rebel Fox Gaming. Todos los derechos reservados.
        </Typography>
      </Box>
    </Box>
  );
}